import { useEffect } from 'react';
import type { ShoppingCartDTO } from '@t/api.types';
import type {
  CheckoutStep,
  CheckoutStepStatus,
  CustomerInfoFormValues,
  InstallationFormValues,
  PhoneFormValues,
} from '@t/checkout.types';
import { useCheckoutStore } from '@store/checkoutStore';
import { hasPhoneInCart, hasInstallationRequiredInCart } from '@utils/cartUtils';
import { activeSteps, buildDefaultStepStatuses } from '@components/Checkout/utils/stepConfig';
import { useShallow } from 'zustand/shallow';

export interface ReconcileInputs {
  cart: ShoppingCartDTO | null | undefined;
  customerInfo: CustomerInfoFormValues | null;
  phoneInfo: PhoneFormValues | null;
  installationInfo: InstallationFormValues | null;
  storeStepStatuses: Record<CheckoutStep, CheckoutStepStatus>;
  storeActiveStep: CheckoutStep;
  storePreviousActiveStep: CheckoutStep | null;
}

export interface ReconcileResult {
  stepStatuses: Record<CheckoutStep, CheckoutStepStatus>;
  activeStep: CheckoutStep;
  hasPhonePlan: boolean;
  hasInstallationRequired: boolean;
}

const isCompletedSignal = (step: CheckoutStep, inputs: ReconcileInputs, hasPhonePlan: boolean): boolean => {
  switch (step) {
    case 'customer':
      return inputs.customerInfo !== null;
    case 'phone':
      return hasPhonePlan && inputs.phoneInfo !== null;
    case 'installation':
      return inputs.cart?.installationDetails != null;
    case 'review':
      return false;
  }
};

export const useReconcileCheckoutSteps = (inputs: ReconcileInputs): ReconcileResult => {
  const { storeHasPhonePlan, setHasPhonePlan, storeHasInstallationRequired, setHasInstallationRequired } = useCheckoutStore(
    useShallow((s) => ({
      storeHasPhonePlan: s.hasPhonePlan,
      setHasPhonePlan: s.setHasPhonePlan,
      storeHasInstallationRequired: s.hasInstallationRequired,
      setHasInstallationRequired: s.setHasInstallationRequired,
    }))
  );

  const hasPhonePlan = hasPhoneInCart(inputs.cart ?? null);
  const hasInstallationRequired = hasInstallationRequiredInCart(inputs.cart ?? null);
  const visible = activeSteps({ hasPhonePlan, hasInstallationRequired });

  useEffect(() => {
    if (storeHasPhonePlan !== hasPhonePlan) setHasPhonePlan(hasPhonePlan);
  }, [hasPhonePlan, storeHasPhonePlan, setHasPhonePlan]);

  useEffect(() => {
    if (storeHasInstallationRequired !== hasInstallationRequired) setHasInstallationRequired(hasInstallationRequired);
  }, [hasInstallationRequired, storeHasInstallationRequired, setHasInstallationRequired]);

  const isInstallationEditInProgress =
    visible.includes('installation') &&
    inputs.storeActiveStep === 'installation' &&
    inputs.storeStepStatuses.installation === 'active' &&
    inputs.installationInfo === null &&
    inputs.cart?.installationDetails != null;

  const isComplete = (step: CheckoutStep): boolean => {
    if (step === 'installation' && isInstallationEditInProgress) return false;
    return isCompletedSignal(step, inputs, hasPhonePlan) || inputs.storeStepStatuses[step] === 'completed';
  };

  const firstIncompleteIdx = visible.findIndex((step) => !isComplete(step));

  const final = buildDefaultStepStatuses();
  visible.forEach((step, idx) => {
    if (isComplete(step)) {
      final[step] = 'completed';
    } else if (idx === firstIncompleteIdx) {
      final[step] = 'active';
    } else {
      final[step] = 'pending';
    }
  });
  (Object.keys(final) as CheckoutStep[]).forEach((step) => {
    if (!visible.includes(step)) final[step] = 'pending';
  });

  const editingStep = inputs.storePreviousActiveStep !== null ? inputs.storeActiveStep : null;
  if (editingStep && visible.includes(editingStep)) {
    final[editingStep] = 'active';
  }
  if (isInstallationEditInProgress) {
    final.installation = 'active';
  }

  let activeStep: CheckoutStep;
  if (editingStep && visible.includes(editingStep)) {
    activeStep = editingStep;
  } else if (isInstallationEditInProgress) {
    activeStep = 'installation';
  } else {
    const storeIdx = visible.indexOf(inputs.storeActiveStep);

    if (storeIdx === -1) {
      activeStep = firstIncompleteIdx !== -1 ? visible[firstIncompleteIdx] : visible[visible.length - 1];
    } else if (firstIncompleteIdx !== -1 && storeIdx > firstIncompleteIdx) {
      activeStep = visible[firstIncompleteIdx];
    } else {
      activeStep = inputs.storeActiveStep;
    }
  }

  return { stepStatuses: final, activeStep, hasPhonePlan, hasInstallationRequired };
};
